<script>
import appConfig from '@src/app.config'
import api from '@services/api'

import Report from '@layouts/report.vue'
import ReportSlide from '@layouts/report-slide.vue'

import SlideHandler from '@components/slide-handler.vue'

import Overview from './overview.vue';
import Searches from './searches.vue';
import Guides from './guides.vue';
import ConversionWebsiteReferrals from './conversion-website-referrals.vue';
import ConversionPageViews from './conversion-page-views.vue';
import Summary from './summary.vue';
import Contact from './contact.vue';

const DEFAULT_CONVERSION = {
  pageViews: { acitve: false },
  websiteReferrals: { acitve: false },
  instagramReferrals: { acitve: false },
  companySize: { acitve: false }
};

export default {
  components: { Report, ReportSlide, SlideHandler },
  props: {
    id : {
      type: [Number,String],
      required: false,
      default: null
    }
  },
  data() {
    return {
      report: null,

      isLoading: true,

      hasError: false,

      modal: null
    };
  },
  computed: {
    fullReport() {
      const report = this.report;
      if(!report) {
        return report;
      }
      report.configuration.conversion = report.configuration.conversion? { ...DEFAULT_CONVERSION, ...this.report.configuration.conversion } : DEFAULT_CONVERSION;
      return report;
    },
    conversion() {
      return this.fullReport? this.fullReport.configuration.conversion : {}
    },
    filteredSlides() {
      return !this.report? [] : this.slides.filter((slide) => {
        return !slide.shouldRender || slide.shouldRender.call(this);
      });
    },
    hasConversion() {
      return this.conversion.websiteReferrals.active === true || this.conversion.pageViews.active === true;
    },
    slides() {
      return [
        {
          id: 'overview',
          component: Overview,
          help: {
            text: 'This is text'
          }
        },
        {
          id: 'searches',
          component: Searches,
          shouldRender: () => {
            return this.report.data.searches && this.report.data.searches.length;
          }
        },
        {
          id: 'guides',
          component: Guides
        },
        {
          id: 'conversion-website-referrals',
          component: ConversionWebsiteReferrals,
          shouldRender: () => {
            return this.conversion.websiteReferrals.active === true && !this.conversion.pageViews.active;
          }
        },
        {
          id: 'conversion-page-views',
          component: ConversionPageViews,
          shouldRender: () => {
            return this.conversion.pageViews.active === true;
          }
        },
        {
          id: 'summary',
          component: Summary,
          shouldRender: () => {
            if (this.report.configuration.summaryActive === undefined) {
              return true
            }

            return this.report.configuration.summaryActive
          }
        },
        {
          id: 'contact',
          component: Contact,
          shouldRender: () => {
            return !!this.report.data.contact;
          }
        }
     ];
    }
  },
  watch: {
    report() {
      if(this.report) {
        this.$i18n.locale = this.report.configuration.language || 'sv';
      // this.locale = 'sv';
        // this.setLocale('sv');
        document.title = appConfig.title + ' – ' + this.report.customer.name + ' – ' + this.report.from_date.replace(/-/g,'')  + '-' + this.report.to_date.replace(/-/g,'');
      }
    }
  },
  async created() {
    if(this.id) {
      const start = new Date().getTime();
      this.report = await this.fetchReport().catch(() => {
        this.hasError = true;
      });
      window.setTimeout(() => {
        this.isLoading = false;
      }, Math.max(0, 1500 - (new Date().getTime() - start)));
    } else {
      this.isLoading = false;
    }
  },
  methods: {
    getFlatImages() {
      return this.report.data.places.map((p) => p.images).reduce((acc, val) => acc.concat(val), []);
      // .flat();
    },
    getImageFromIndex(index) {
      const images = this.getFlatImages();
      return images[index % images.length];
    },
    fetchReport() {
      if(!this.id) {
        return null;
      }
      return api.get(`/reports/${this.id}`).then((response) => {
        return response.data.data
      })
    },
    openModal(modal) {
      this.modal = modal;
    }
  }
}
</script>
<template>
  <div>
    <SlideHandler v-if="!isLoading && report" >
      <template v-slot="slotProps">
        <Report
          :report="fullReport"
          :slides="filteredSlides"
          :active-slide="slotProps.slide"
          :go-to-slide="slotProps.goToSlide"
          :is-modal-open="!!modal"
          :show-dates="slotProps.slide > 0"
        >
          <template v-for="(slide, slideIndex) in filteredSlides">
            <ReportSlide
              :key="slide.id"
              :report="fullReport"
              :is-active="slideIndex === slotProps.slide"
              :subslide="slideIndex !== slotProps.slide ? null : slotProps.subslide"
              :index="slideIndex"
              :slide="slide"
              :active-index="slotProps.slide"
              :background="getImageFromIndex(slideIndex)"
              @modal="openModal"
            />
          </template>
        </Report>
      </template>
    </SlideHandler>
    <transition name="modal">
      <BaseModal v-if="modal" v-bind="modal" @close="modal = null" />
    </transition>
    <transition name="fade">
      <div v-if="!report || isLoading" :class="[$style['no-report'],!isLoading && hasError ? $style.error : null]">
        <div :class="['logo-wrapper',$style.logo]">
          <img src="@assets/images/logo.svg" alt="Thatsup" />
        </div>
        <transition name="loading-fade">
          <div v-if="isLoading" :class="$style.loading">
            <div :class="$style.loader"></div>
          </div>
        </transition>
        <div v-if="!isLoading && hasError">
          <p :class="$style.errorText">That report doesn't exist.</p>
        </div>
      </div>
    </transition>
  </div>
</template>
<style lang="scss" module>
@import '@design';
.no-report {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  background: linear-gradient(-90deg, $color-body-bg 0, $color-body-bg-transparent 100% );
  background-repeat: no-repeat;
  background-position: top right;
  background-size: 400% 100%;

  .logo {
    @keyframes logo-appear {
      0% { height: 0; opacity: 0; }
      100% { height: 44px; opacity: 1; }
    }

    overflow: hidden;
    animation: logo-appear 0.5s ease-in-out;
  }
}
.errorText {
  &, a {
    color: #f9a4a4;
  }
}
.error {
  .logo {
    @keyframes shake {
      10%, 90% {
        transform: translate3d(-1px, 0, 0);
      }

      20%, 80% {
        transform: translate3d(2px, 0, 0);
      }

      30%, 50%, 70% {
        transform: translate3d(-4px, 0, 0);
      }

      40%, 60% {
        transform: translate3d(4px, 0, 0);
      }
    }

    transform: translate3d(0, 0, 0);
    animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
  }
  .errorText {
    @keyframes error-appear {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }

    position: absolute;
    left: 50%;
    max-width: 80%;
    overflow: hidden;
    transform: translateX(-50%);
    animation: error-appear 0.8s ease-in-out;
  }
}
.loading {
  height: 34px;
  .loader {
    display: inline-block;
    width: 34px;
    height: 34px;
    margin-bottom: 35px;
    background-image: url("data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPjxyYWRpYWxHcmFkaWVudCBpZD0iYSIgY3g9Ijk3LjQ4MyUiIGN5PSIyOC41NzMlIiBncmFkaWVudFRyYW5zZm9ybT0ibWF0cml4KC45OTgzMDM4NCAtLjA1ODIxODk2IC4wMjM1MjI3OSAuNDAzMzU0NjggLS4wMDUwNjggLjIyNzIzNikiIHI9IjE2OC4zMzYlIj48c3RvcCBvZmZzZXQ9IjAiIHN0b3AtY29sb3I9IiNmZmYiLz48c3RvcCBvZmZzZXQ9Ii40ODQyNiIgc3RvcC1jb2xvcj0iI2ZmZiIgc3RvcC1vcGFjaXR5PSIuODE2Ii8+PHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjZmZmIiBzdG9wLW9wYWNpdHk9IjAuMyIvPjwvcmFkaWFsR3JhZGllbnQ+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJtMCAwaDI0djI0aC0yNHoiIGZpbGw9Im5vbmUiLz48cGF0aCBkPSJtMTIgMjNjLTYuMDc1MTMyMjUgMC0xMS00LjkyNDg2NzgtMTEtMTEgMC02LjA3NTEzMjI1IDQuOTI0ODY3NzUtMTEgMTEtMTEgNi4wNzUxMzIyIDAgMTEgNC45MjQ4Njc3NSAxMSAxMSAwIDYuMDc1MTMyMi00LjkyNDg2NzggMTEtMTEgMTF6IiBzdHJva2U9InVybCgjYSkiIHN0cm9rZS1kYXNoYXJyYXk9IjQ4LjMzIDY5LjExNyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+");
    background-repeat: no-repeat;
    background-size: cover;
    animation-name: spin;
    animation-duration: 500ms;
    animation-timing-function: linear;
    animation-iteration-count: infinite;

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }
  }
}
</style>
<style lang="scss">
@import '@design';
.fade-enter-active, .fade-leave-active {
  transition: all 1s  ease-in-out;
  > * {
    transition: all 1s  ease-in-out;
  }
}
.fade-enter, .fade-leave-to {
  background-size: 0% 100%;
  > * {
    opacity: 0;
  }
}
.loading-fade-enter-active, .loading-fade-leave-active {
  overflow: hidden;
  transition: all 0.5s  ease-in-out;
}
.loading-fade-enter, .loading-fade-leave-to {
  height: 0;
  opacity: 0;
}
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease-in-out;
  .modal--content {

  }
}
.modal-enter, .modal-leave-to {
  opacity: 0;
  .modal--content {
    transform: translateY(100%);

    @media screen and (min-width: $size-breakpoint-sm) {
      transform: scale(0.8);
    }
  }
}
</style>
